import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, ChevronDown, ChevronUp, Search } from "lucide-react";
import { useSearchCities } from "@/lib/api";
import { CityList } from "../CityList";
import { useT } from "@/lib/i18n";

interface CitySelectorProps {
  availableCityIds?: Set<string>;
  selectedCity: string;
  onCitySelect: (city: string) => void;
  initiallyCollapsed?: boolean;
}

export function CitySelector({
  availableCityIds,
  selectedCity,
  onCitySelect,
  initiallyCollapsed = false,
}: CitySelectorProps) {
  const { t } = useT();
  const [showSelector, setShowSelector] = useState(!initiallyCollapsed);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: citiesData } = useSearchCities(debouncedQuery, selectedCity);
  const cities = citiesData?.data || [];

  const selectedCityData = cities.find((c: any) => c.id === selectedCity);

  const handleCitySelect = (cityValue: string) => {
    onCitySelect(cityValue);
    setShowSelector(false);
    setSearchQuery("");
  };

  return (
    <div className="mb-6">
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between mb-4 group"
      >
        <div className="flex items-center">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${
              selectedCity ? "bg-primary-500/20" : "bg-dark-elevated dark:bg-neutral-800"
            }`}
          >
            <MapPin
              className={`w-5 h-5 transition-colors ${
                selectedCity ? "text-primary-500" : "text-dark-muted dark:text-neutral-400"
              }`}
            />
          </div>
          <div className="text-left">
            <div className="text-xs text-dark-muted dark:text-neutral-400 mb-0.5">{t('common.labels.city')}</div>
            <div className="text-sm font-semibold dark:text-neutral-100">
              {selectedCityData
                ? selectedCityData.name
                : t('citySelection.title')}
            </div>
          </div>
        </div>
        {selectedCity &&
          (showSelector ? (
            <ChevronUp className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          ) : (
            <ChevronDown className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          ))}
      </button>

      {(!selectedCity || showSelector) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
        >
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-muted" />
            <input
              type="text"
              placeholder={t('citySelection.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-dark-elevated border border-divider focus:border-primary-500 focus:outline-none transition-colors"
            />
          </div>

          <CityList
            cities={cities}
            selectedCityId={selectedCity}
            onSelectCity={handleCitySelect}
            availableCityIds={availableCityIds}
          />
        </motion.div>
      )}
    </div>
  );
}
